/**
 * What a FlexiCo template JSON may contain for "Programmatic add".
 *
 * One spec drives the validator and the sample file. The user guide, field reference and
 * agent guide on SharePoint (USER_GUIDE_URL) describe these same rules; update them together. Every rule mirrors the manual form
 * (AddOrEditTemplateComponent / ParameterDetails); the payload is sent to the same
 * createFlexicoTemplate API the form uses.
 */
import { APPROVER_TYPES, UPLOADER_TYPES } from "../Constants";

export const CONTICO_JOB_LABEL = 'contico_job';

// Used only if the live list (form metadata API) cannot be loaded.
export const FALLBACK_TRIGGER_TYPES = ['Upload file with parameters', 'Upload file only', 'Parameters only', 'None'];
export const UPLOAD_WITH_PARAMETERS = 'Upload file with parameters';
export const UPLOAD_TRIGGER_TYPES = ['Upload file with parameters', 'Upload file only'];
export const NO_APPROVAL_TRIGGER_TYPES = ['Parameters only', 'None'];
// User guide, field reference and agent guide on the ATEAM Technology SharePoint.
export const USER_GUIDE_URL = 'https://amazon.sharepoint.com/sites/ATEAMTechnology/Shared%20Documents/Forms/AllItems.aspx?id=%2Fsites%2FATEAMTechnology%2FShared%20Documents%2FATEAM%20%2D%20UI%2FFlexiCo%2FProgrammatic%20Create&viewid=25480572%2Db18a%2D42fb%2D8687%2D75da176336d3&d=w08a8e356faba49ca82fa1c7c88929960&csf=1&CID=dea2a6a4%2D3c26%2D460c%2Db4b2%2D15e74b7785ab&FolderCTID=0x012000EBBF3158AF53994181048CC4FB631ACD';

export const TEMPLATE_FIELDS = [
    { key: 'template_name', type: 'string', required: true, form: 'FlexiCo template name',
      description: 'Unique name of the template. Must not be used by another template, in this file or already saved.' },
    { key: 'label', type: 'string', form: 'Label',
      description: 'Name shown on the app page. Defaults to template_name.' },
    { key: 'description', type: 'string', form: 'Description', description: 'Free text shown on the execute page.' },
    { key: 'trigger_type', type: 'string', required: true, form: 'Input type',
      description: 'What the user provides when running the template. One of the input types listed below.' },
    { key: 'upload_mandatory', type: 'boolean', form: 'File upload Mandatory',
      description: 'Only for "Upload file with parameters": the user must attach a file.' },
    { key: 'critical_data', type: 'boolean', form: 'Critical data',
      description: 'Marks the template as handling critical data. Also decides which account contico_job is checked in.' },
    { key: 'sox_approved', type: 'boolean', form: 'Enable 2 touch approval',
      description: 'Two-touch approval: needs at least 2 approvers, or 1 LDAP approver group. For upload input types it also turns approval_required on.' },
    { key: 'sox_evidence', type: 'string', form: 'notes (2 touch approval)', description: 'Notes for 2 touch approval. Only used when sox_approved is true.' },
    { key: 'approval_required', type: 'boolean', form: 'Approval required',
      description: 'Runs need approval. Requires at least one approver. Not allowed for "Parameters only" and "None".' },
    { key: 'approvers', type: 'array', form: 'Approver(s) Details',
      description: '[{ "alias", "type", "column_index" }]. type is APPROVER (a user) or APPROVER_LDAP (a group). column_index is the order of approval, starting at 0.' },
    { key: 'uploaders', type: 'array', form: 'Trigger permissions',
      description: '[{ "alias", "type" }]: who may run the template. type is UPLOADER (a user) or UPLOADER_LDAP (a group).' },
    { key: 'parameters', type: 'array', required: true, form: 'Parameters',
      description: 'The inputs of the template (see Parameters). Must include contico_job.' },
    { key: 'custom_attributes', type: 'array', form: 'Notification, email, trigger message, parameter groups',
      description: '[{ "attribute_type", "attribute" }]. See Custom attributes.' },
];

export const PARAMETER_FIELDS = [
    { key: 'label', type: 'string', required: true, form: 'Parameter key', description: 'Key of the parameter, unique within the template. Passed to the job under this name.' },
    { key: 'field_type', type: 'string', required: true, form: 'Type', description: 'One of the parameter types below.' },
    { key: 'field_values', type: 'array of strings', form: 'Values', description: 'Meaning depends on the type (see Parameter types).' },
    { key: 'display_name', type: 'string', form: 'Display name', description: 'Label shown to the user. Defaults to label.' },
    { key: 'info', type: 'object', form: 'Info', description: 'Help shown next to the parameter: { "description": "…" } or { "url": "https://…" }, not both.' },
    { key: 'is_mandatory', type: 'boolean', form: 'Mandatory', description: 'The user must fill it in. Cannot be combined with limit_permissions.' },
    { key: 'is_hidden', type: 'boolean', form: 'Hidden in the execution page', description: 'Sent to the job but not shown to the user.' },
    { key: 'limit_permissions', type: 'boolean', form: 'Limit who can set parameter', description: 'Only the people in permissions may change it. Cannot be combined with is_mandatory.' },
    { key: 'permissions', type: 'array', form: 'Who can set parameter', description: '[{ "alias", "type" }] with type UPLOADER or UPLOADER_LDAP. Only used with limit_permissions.' },
    { key: 'source_database', type: 'string', form: 'Source database', description: 'Only for dynamic_select / dynamic_multi_select. The only option is FAST (the default).' },
    { key: 'display_index', type: 'number', form: 'Order', description: 'Position on the execute page. Defaults to the order in this list.' },
];

export const PARAMETER_TYPES = {
    select:                { values: 'The options; the user picks one.', example: ['US', 'EU', 'JP'] },
    multi_select:          { values: 'The options; the user picks any number.', example: ['US', 'EU', 'JP'] },
    radio_group:           { values: 'The options, shown as radio buttons; the user picks one.', example: ['Actual', 'Forecast'] },
    checkbox_group:        { values: 'The options, shown as checkboxes; the user picks any number.', example: ['Ledger A', 'Ledger B'] },
    nested_checkbox_group: { values: 'The child options under a parent checkbox (display_name is the parent). At least one.', example: ['Q1', 'Q2'] },
    input:                 { values: 'Ignored: the user types the value. Sent as [" "].', example: [] },
    numeric_up_down:       { values: 'At most one value: the default number, digits only.', example: ['5'] },
    fixed_inputs:          { values: 'Fixed values passed to the job unchanged; the user cannot edit them. At least one.', example: ['some_value'] },
    dynamic_select:        { values: 'Exactly one value: a SQL query (FAST) whose results become the options. {other_param} placeholders refer to other parameters.', example: ['SELECT ledger_name FROM my_schema.my_table'] },
    dynamic_multi_select:  { values: 'Like dynamic_select, but the user picks any number.', example: ['SELECT ledger_name FROM my_schema.my_table'] },
    tree:                  { values: 'Exactly one value: a JSON string of [{ "label", "value", "children": [...] }]. Only leaves can be picked.', example: ['[{"label":"Americas","children":[{"label":"US","value":"US"}]}]'] },
    date_picker:           { values: 'Ignored: the user picks a date. Sent as [" "].', example: [] },
    date_time_picker:      { values: 'Ignored: the user picks a date and time. Sent as [" "].', example: [] },
};
export const USER_ENTERED_TYPES = ['input', 'date_picker', 'date_time_picker'];
export const DYNAMIC_TYPES = ['dynamic_select', 'dynamic_multi_select'];
export const SOURCE_DATABASES = ['FAST'];

export const NOTIFICATION_PREFERENCES = {
    DEFAULT: 'Send all (default)',
    DISABLE_ALL: 'No notification updates from the FlexiCo system',
    DISABLE_ALL_WITH_CRITICAL: 'No notifications, including the review and approval emails',
};

export const PLACEHOLDER_HELP = 'Supports {template_name}, {file_id}, {current_quarter}, {previous_quarter}, {month}, {previous_month}, ' +
    '{year}, {current_year}, {previous_year}, {previous_month_year}, {current_month_year}, {label}, {wb_type}, {wb_status}, ' +
    '{deliverable_reference} and any parameter label.';

export const CUSTOM_ATTRIBUTE_TYPES = {
    NOTIFICATION_PREFERENCE: { form: 'Notification Preference', description: `One of ${Object.keys(NOTIFICATION_PREFERENCES).join(', ')}.` },
    EMAIL_PREFERENCE_TITLE: { form: 'Notification email Title', description: `Subject of notification emails. ${PLACEHOLDER_HELP}` },
    APPROVAL_PREFERENCE_TITLE: { form: 'Approval title', description: `Subject of approval emails. ${PLACEHOLDER_HELP}` },
    APPROVAL_PREFERENCE_DESCRIPTION: { form: 'Approval Description', description: `Body text of approval emails. ${PLACEHOLDER_HELP}` },
    TRIGGER_MESSAGE: { form: 'Trigger message', description: 'Message shown when a user first triggers the template.' },
    GROUPING_PREFERENCE: { form: 'Parameter groups',
        description: 'Groups parameters on the execute page. attribute is an object: { "group_info": { "<group id>": { "group_name", "group_order", "is_mandatory" } }, "parameter_mapping_by_label": { "<parameter label>": "<group id>" } }.' },
};
export const UI_ONLY_ATTRIBUTE_TYPES = ['TANC_REVIEW_PREFERENCE_TITLE', 'TANC_REVIEW_PREFERENCE_DESCRIPTION'];

// Filled in by the page, exactly as the form does, or not settable from the form at all.
// Accepted in the file (so a template copied from "Download app" works) but ignored.
export const AUTO_SET_TEMPLATE_KEYS = {
    app_id: 'the app you are adding to',
    step_function_name: 'the FlexiCo Step Function for this stage',
    status: '"Template created"',
    created_by: 'you',
    last_updated_by: 'you',
    flex_id: 'a new id', mapping_id: 'a new id',
    created_time_stamp: 'now', last_updated_time_stamp: 'now',
    group_id: 'not set by the form', group_name: 'not set by the form', next_step: 'not set by the form',
    approval_days: 'not set by the form', file_types: 'not set by the form',
    ripples: 'add ripples in Settings after creating', tanc: 'set up TANC in Settings after creating',
};
export const AUTO_SET_PARAMETER_KEYS = ['field_id', 'id', 'group_id', 'group_name', 'group_order'];

export const APPROVER_TYPE_VALUES = Object.values(APPROVER_TYPES);
export const UPLOADER_TYPE_VALUES = Object.values(UPLOADER_TYPES);

export const SAMPLE_FILE = {
    templates: [
        {
            template_name: 'my_team_ledger_refresh',
            label: 'Ledger refresh',
            description: 'Refreshes the ledger extract for the selected region and period.',
            trigger_type: 'Parameters only',
            critical_data: false,
            uploaders: [{ alias: 'my-team-ldap', type: 'UPLOADER_LDAP' }],
            parameters: [
                { label: CONTICO_JOB_LABEL, display_name: CONTICO_JOB_LABEL, field_type: 'fixed_inputs', field_values: ['my_glue_job_name'] },
                { label: 'region', display_name: 'Region', field_type: 'select', field_values: ['US', 'EU', 'JP'], is_mandatory: true,
                  info: { description: 'Region to refresh.' } },
                { label: 'as_of_date', display_name: 'As of date', field_type: 'date_picker', field_values: [] },
            ],
            custom_attributes: [
                { attribute_type: 'NOTIFICATION_PREFERENCE', attribute: 'DEFAULT' },
                { attribute_type: 'TRIGGER_MESSAGE', attribute: 'Refresh started. You will get an email when it finishes.' },
            ],
        },
        {
            template_name: 'my_team_journal_upload',
            label: 'Journal upload',
            trigger_type: 'Upload file with parameters',
            upload_mandatory: true,
            sox_approved: true,
            sox_evidence: 'Two-touch review by the controllership team.',
            approval_required: true,
            approvers: [
                { alias: 'reviewer-alias', type: 'APPROVER', column_index: 0 },
                { alias: 'approver-alias', type: 'APPROVER', column_index: 1 },
            ],
            uploaders: [{ alias: 'uploader-alias', type: 'UPLOADER' }],
            parameters: [
                { label: CONTICO_JOB_LABEL, display_name: CONTICO_JOB_LABEL, field_type: 'fixed_inputs', field_values: ['my_upload_glue_job'] },
                { label: 'ledger', display_name: 'Ledger', field_type: 'dynamic_select', source_database: 'FAST',
                  field_values: ['SELECT DISTINCT ledger_name FROM my_schema.ledgers'], is_mandatory: true },
                { label: 'notes', display_name: 'Notes', field_type: 'input', field_values: [] },
            ],
            custom_attributes: [
                { attribute_type: 'APPROVAL_PREFERENCE_TITLE', attribute: 'Approval needed: {template_name} for {current_quarter}' },
            ],
        },
    ],
};
